import { getObjectById, IEventrActivity, tryParseJSON } from '@eventr/common'
import {
    AuthImage,
    ColoursObj,
    Gutters,
    MainButton,
    Typography,
} from '@eventr/common-storybook'
import { Box, Stack } from '@mui/material'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { useGlobalRouter } from '../../feat/globalRouter/useGlobalRouter'
import { NavigationView } from '../../feat/layout/NavigationView'
import { MarkdownRow } from '../../home/components/summary/markdownRow'
import {
    cardTranslation,
    editItineraryTranslation,
    sectionTranslation,
} from '../../i18n/translateHelpers'
import { Paths } from '../../routes'
import { useBoundStore } from '../../state/bound.store'
import { useRouteGuardContext } from '../../state/routeGuardProvider'
import { FlightPassengers } from '../componentCards/flightComponents/flightPassengers'
import { useMatchTagsAndUsers } from '../componentCards/hooks/useMatchTagsAndUsers'
import { CategoryList } from '../componentCards/subComponents/categoryList'
import { RowInfoAddress } from '../componentCards/subComponents/rowInfoAddress'
import { RowInfoFiles } from '../componentCards/subComponents/rowInfoFiles'
import { RowInfoNotes } from '../componentCards/subComponents/rowInfoNotes'
import { RowInfoStandart } from '../componentCards/subComponents/RowInfoStandart'
import { RowInfoWeb } from '../componentCards/subComponents/rowInfoWeb'
import { SectionHeader } from '../componentCards/subComponents/sectionHeader'
import { VisibilityIcon } from '../componentCards/subComponents/visibilityRow'
import { useFormatValue } from '../hooks/useFormatValue'
import { getAllAttendees } from '../utils/activities'
import { useToolbarChat } from './hooks/useToolbarChat'

export const ActivityPage = () => {
    const { _id } = useParams()
    const { formatValue } = useFormatValue()
    const { matchTagsAndUser } = useMatchTagsAndUsers()
    const { goBack, pushPath } = useGlobalRouter()
    const { isAllowed } = useRouteGuardContext()
    const { activitiesSection } = useBoundStore(
        useShallow((s) => {
            return {
                activitiesSection: (s?.selectedItinerary?.v2 ?? []).find(
                    (s) => s.sectionID === 8
                ),
            }
        })
    )

    const activity = useMemo(() => {
        return getObjectById(
            _id ?? '',
            activitiesSection?.data?.activities ?? []
        ) as IEventrActivity
    }, [_id, activitiesSection?.data?.activities])

    const allGuests = useMemo(() => {
        return getAllAttendees(activity)
    }, [activity])

    const guest = useMemo(() => {
        if ((activity?.attendees_list?.length ?? 0) === 0) {
            return null
        }
        return activity?.attendees_list?.find((passenger) => {
            return passenger.users?.some((user) => {
                return matchTagsAndUser(user)
            })
        })
    }, [activity, matchTagsAndUser])

    const personalFiles = useMemo(() => {
        if (!guest?.documents) return []
        return tryParseJSON(guest.documents, []) as string[]
    }, [guest])

    const files = useMemo(() => {
        if (!activity?.documents) return []
        return tryParseJSON(activity.documents, []) as string[]
    }, [activity])

    const showPersonalSection = useMemo(() => {
        if (guest?.notes || personalFiles.length > 0) {
            return true
        }
        return false
    }, [guest, personalFiles])

    const attachmentsCount = useMemo(() => {
        let count = 0
        if (activity?.website) count++
        if (activity?.additional_information) count++
        if (files.length) count++
        return count
    }, [activity, files])

    const onEditClicked = () => {
        pushPath(Paths.editGeneralActivity(activity._id))
    }

    const allowedEdit = useMemo(() => {
        if (activity?._og_code) {
            return false
        }
        return isAllowed(['allowedEdit'])
    }, [activity._og_code, isAllowed])

    const chat = useToolbarChat(
        [activity.activity_name ?? 'Activity chat'],
        allGuests,
        8,
        activity._id
    )
    return (
        <NavigationView
            navigationBarTitle={activity?.activity_name}
            backButtonAction={() => {
                goBack()
            }}
            navigationBarTrailing={chat !== null ? [chat] : undefined}
        >
            <Gutters topPadding="0px">
                <Stack spacing={'8px'}>
                    {activity?.visibility === 'Private' && (
                        <Stack
                            paddingBottom={'16px'}
                            direction={'row'}
                            gap={'8px'}
                        >
                            <VisibilityIcon visibility={activity?.visibility} />
                            <Typography
                                color={ColoursObj.core.secondary}
                                variant="bodySubtextBold"
                            >
                                {cardTranslation('Private')}
                            </Typography>
                        </Stack>
                    )}
                    {/* Count down goes here  */}
                    {activity?.images?.[0]?.url && activity?.isAdvance && (
                        <Box borderRadius={'8px'} overflow={'hidden'}>
                            <AuthImage
                                width={'100%'}
                                component="img"
                                addLoading
                                loading="lazy"
                                src={activity?.images?.[0]?.url}
                            ></AuthImage>
                        </Box>
                    )}
                    <Typography variant="bodySubtext">
                        {activity?.description &&
                            MarkdownRow({ value: activity?.description })}
                    </Typography>
                    {activity?.start && (
                        <RowInfoStandart
                            icon="Right"
                            bubbleColour={ColoursObj.core.secondary}
                            middle={
                                cardTranslation('Start') +
                                ': ' +
                                formatValue(activity?.start, 'datetime')
                            }
                        />
                    )}
                    {activity?.end && activity?.end !== activity?.start && (
                        <RowInfoStandart
                            icon="Left"
                            bubbleColour={ColoursObj.core.secondary}
                            middle={
                                cardTranslation('End') +
                                ': ' +
                                formatValue(activity?.end, 'datetime')
                            }
                        />
                    )}
                    {activity?.location && (
                        <RowInfoStandart
                            icon="Location"
                            bubbleColour={ColoursObj.coreBrights.secondary}
                            middle={activity?.location}
                        />
                    )}
                    {activity?.address && (
                        <RowInfoAddress address={activity?.address} />
                    )}
                    {attachmentsCount > 0 && (
                        <SectionHeader title={cardTranslation('Attachments')} />
                    )}
                    {activity?.website && (
                        <RowInfoWeb website={activity?.website} />
                    )}
                    {activity?.additional_information && (
                        <RowInfoNotes
                            notes={activity?.additional_information}
                        />
                    )}
                    {activity?.documents && <RowInfoFiles files={files} />}
                    {showPersonalSection && (
                        <SectionHeader
                            title={cardTranslation('Personal Items')}
                        />
                    )}
                    {guest?.notes && <RowInfoNotes notes={guest?.notes} />}
                    {personalFiles.length > 0 && (
                        <RowInfoFiles files={personalFiles} />
                    )}

                    <CategoryList categories={activity?.categories} />
                    {allGuests.length > 0 && (
                        <FlightPassengers
                            passengers={allGuests}
                            title={cardTranslation('Guests')}
                        />
                    )}
                    <Box></Box>

                    {allowedEdit && (
                        <MainButton
                            onClick={() => {
                                onEditClicked()
                            }}
                        >
                            {editItineraryTranslation(
                                'Edit Section',
                                'section',
                                {
                                    sectionName: sectionTranslation(
                                        'Activities',
                                        {
                                            count: 1,
                                        }
                                    ),
                                }
                            )}{' '}
                        </MainButton>
                    )}
                </Stack>
            </Gutters>
        </NavigationView>
    )
}
